import React, { useCallback, useEffect, useState, Component } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Search, Eye, Pencil, MoreHorizontal, X, AlertTriangle, ChevronLeft, ChevronRight, Loader2, Check, Cpu, Copy, Video, Radio } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllDevices, createDevice, createCradleDevice, updateDeviceStatus, deleteDevice, updateDeviceCamera, getDeviceCameraStream } from '../api/devices';
import { apiGet } from '../api/client';
// --- Types ---
interface Device {
  id: string;
  deviceUid: string;
  serialNumber: string;
  deviceType: string;
  status: 'active' | 'inactive' | 'pending';
  firmwareVersion: string;
  hardwareVersion: string;
  lastSeenAt?: string;
  createdAt: string;
  assignedUser?: {
    id: string;
    fullName: string;
    email: string;
  };
  cameraName?: string | null;
  streamType?: 'mjpeg' | 'hls' | 'iframe' | null;
  streamUrl?: string | null;
  posterUrl?: string | null;
  isLive?: boolean;
}
// --- Main Component ---
export function Devices() {
  const {
    token
  } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [totalDevices, setTotalDevices] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('newest');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Modals & Panels State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCradleModalOpen, setIsCradleModalOpen] = useState(false);
  const [editDevice, setEditDevice] = useState<Device | null>(null);
  const [deleteDeviceObj, setDeleteDeviceObj] = useState<Device | null>(null);
  const [viewDevice, setViewDevice] = useState<Device | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);
  // Fetch Data
  const fetchDevices = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: debouncedSearch,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort: sortFilter,
        page,
        limit
      };
      const res = await getAllDevices(token, filters);
      const rawDevices = res.devices || res.data || [];
      const mapped: Device[] = rawDevices.map((d: any) => ({
        id: d.id,
        deviceUid: d.deviceUid || '',
        serialNumber: d.serialNumber || '',
        deviceType: d.deviceType || 'Bella Belt',
        status: d.status || 'inactive',
        firmwareVersion: d.firmwareVersion || '',
        hardwareVersion: d.hardwareVersion || '',
        lastSeenAt: d.lastSeenAt,
        createdAt: d.createdAt || d.activatedAt || '',
        assignedUser: d.assignments?.[0]?.user ? {
          id: d.assignments[0].user.id,
          fullName: d.assignments[0].user.fullName,
          email: d.assignments[0].user.email
        } : d.assignedUser || undefined,
        cameraName: d.cameraName ?? d.camera_name ?? null,
        streamType: d.streamType ?? d.stream_type ?? null,
        streamUrl: d.streamUrl ?? d.stream_url ?? null,
        posterUrl: d.posterUrl ?? d.poster_url ?? null,
        isLive: false
      }));
      setDevices(mapped);
      setTotalDevices(res.total || 0);
      // Fetch live status for each device in parallel
      const liveResults = await Promise.allSettled(mapped.map(d => getDeviceCameraStream(token, d.id)));
      setDevices(prev => prev.map((d, i) => {
        const r = liveResults[i];
        const isLive = r.status === 'fulfilled' ? !!(r.value?.isLive ?? r.value?.live ?? r.value?.data?.isLive) : false;
        return {
          ...d,
          isLive
        };
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to load devices');
    } finally {
      setIsLoading(false);
    }
  }, [token, debouncedSearch, statusFilter, sortFilter, page, limit]);
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);
  // Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(new Set(devices.map(d => d.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleSelectOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard');
  };
  const getRelativeTime = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    const diffMs = new Date().getTime() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };
  const isRecentlyActive = (timestamp?: string) => {
    if (!timestamp) return false;
    const diffMins = (new Date().getTime() - new Date(timestamp).getTime()) / 60000;
    return diffMins <= 5;
  };
  return <div className="relative min-h-full pb-20" data-id="element-506">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-507">
        <div data-id="element-508">
          <h1 className="text-[24px] font-semibold text-white" data-id="element-509">Devices</h1>
          <p className="text-[14px] text-text-secondary mt-1" data-id="element-510">
            {isLoading ? 'Loading...' : `${totalDevices.toLocaleString()} total devices`}
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-511">
          <button onClick={() => setIsCradleModalOpen(true)} className="h-[36px] px-4 bg-surface border border-border text-white font-medium rounded-btn flex items-center gap-2 hover:bg-border transition-colors" data-id="element-512">
            <Radio className="w-4 h-4" data-id="element-513" />
            Create Cradle Device
          </button>
          <button onClick={() => setIsAddModalOpen(true)} className="h-[36px] px-4 bg-accent text-background font-semibold rounded-btn flex items-center gap-2 hover:opacity-90 transition-opacity" data-id="element-514">
            <Plus className="w-4 h-4" data-id="element-515" />
            Add Device
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center justify-between mb-6 gap-4" data-id="element-516">
        <div className="flex items-center gap-3" data-id="element-517">
          <div className="relative" data-id="element-518">
            <Search className="w-4 h-4 text-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" data-id="element-519" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by UID or serial..." className="w-[280px] h-[36px] bg-surface border border-border rounded-input pl-9 pr-4 text-[14px] text-white placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors" data-id="element-520" />
          </div>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8 relative" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-521">
            <option value="all" data-id="element-522">All Status</option>
            <option value="active" data-id="element-523">Active</option>
            <option value="inactive" data-id="element-524">Inactive</option>
            <option value="pending" data-id="element-525">Pending</option>
          </select>

          <select value={sortFilter} onChange={e => setSortFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-526">
            <option value="newest" data-id="element-527">Newest first</option>
            <option value="oldest" data-id="element-528">Oldest first</option>
            <option value="last_seen" data-id="element-529">Last seen</option>
            <option value="status" data-id="element-530">Status</option>
          </select>
        </div>

        <div className="text-[13px] text-text-secondary" data-id="element-531">
          Showing {devices.length > 0 ? (page - 1) * limit + 1 : 0}-
          {Math.min(page * limit, totalDevices)} of {totalDevices} devices
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border rounded-card overflow-hidden" data-id="element-532">
        <div className="overflow-x-auto" data-id="element-533">
          <table className="w-full text-left border-collapse" data-id="element-534">
            <thead data-id="element-535">
              <tr className="bg-surface-elevated border-b border-border h-[44px]" data-id="element-536">
                <th className="px-4 w-[40px]" data-id="element-537">
                  <div className="relative flex items-center" data-id="element-538">
                    <input type="checkbox" className="peer sr-only" checked={devices.length > 0 && selectedIds.size === devices.length} onChange={handleSelectAll} data-id="element-539" />
                    <div className="w-4 h-4 bg-surface border border-border rounded-[4px] peer-checked:bg-accent peer-checked:border-accent flex items-center justify-center transition-colors" data-id="element-540">
                      <Check className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" data-id="element-541" />
                    </div>
                  </div>
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-542">
                  Device
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[120px]" data-id="element-543">
                  Type
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-544">
                  Status
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[200px]" data-id="element-545">
                  Assigned To
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[140px]" data-id="element-546">
                  Last Seen
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-547">
                  Firmware
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-548">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-549">
              {isLoading ? <tr data-id="element-550">
                  <td colSpan={8} className="h-[200px] text-center text-text-secondary" data-id="element-551">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" data-id="element-552" />
                    Loading devices...
                  </td>
                </tr> : devices.length === 0 ? <tr data-id="element-553">
                  <td colSpan={8} className="h-[200px] text-center text-text-secondary" data-id="element-554">
                    No devices found matching your criteria.
                  </td>
                </tr> : devices.map(device => <tr key={device.id} className="h-[56px] border-b border-[#1E1E1E] hover:bg-[#1E1E1E] transition-colors" data-id="element-555">
                    <td className="px-4" data-id="element-556">
                      <div className="relative flex items-center" data-id="element-557">
                        <input type="checkbox" className="peer sr-only" checked={selectedIds.has(device.id)} onChange={() => handleSelectOne(device.id)} data-id="element-558" />
                        <div className="w-4 h-4 bg-surface border border-border rounded-[4px] peer-checked:bg-accent peer-checked:border-accent flex items-center justify-center transition-colors" data-id="element-559">
                          <Check className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" data-id="element-560" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4" data-id="element-561">
                      <div className="flex items-center gap-3" data-id="element-562">
                        <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0" data-id="element-563">
                          <Cpu className="w-4 h-4 text-white" data-id="element-564" />
                        </div>
                        <div className="flex flex-col" data-id="element-565">
                          <div className="flex items-center gap-2" data-id="element-566">
                            <span className="text-[14px] font-medium text-white font-mono" data-id="element-567">
                              {device.deviceUid}
                            </span>
                            <button onClick={() => copyToClipboard(device.deviceUid)} className="text-text-tertiary hover:text-white transition-colors" data-id="element-568">
                              <Copy className="w-3 h-3" data-id="element-569" />
                            </button>
                          </div>
                          <span className="text-[13px] text-text-secondary" data-id="element-570">
                            {device.serialNumber}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 text-[13px] text-text-secondary" data-id="element-571">
                      <div className="flex items-center gap-2" data-id="element-572">
                        <span data-id="element-573">{device.deviceType || 'Bella Belt'}</span>
                        {(device.deviceType || '').toLowerCase().includes('cradle') && <span className="px-2 py-0.5 rounded-badge bg-[rgba(107,33,232,0.12)] text-accent text-[11px] border border-[rgba(107,33,232,0.3)] capitalize" data-id="element-574">
                            cradle
                          </span>}
                      </div>
                    </td>
                    <td className="px-4" data-id="element-575">
                      <div className="flex flex-col gap-1" data-id="element-576">
                        {device.status === 'active' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)] w-fit" data-id="element-577">
                            Active
                          </span>}
                        {device.status === 'inactive' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(138,138,138,0.1)] text-text-secondary text-[12px] w-fit" data-id="element-578">
                            Inactive
                          </span>}
                        {device.status === 'pending' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)] w-fit" data-id="element-579">
                            Pending
                          </span>}
                        {device.isLive && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[11px] border border-[rgba(239,68,68,0.25)] w-fit inline-flex items-center gap-1.5" data-id="element-580">
                            <span className="w-1.5 h-1.5 rounded-full bg-danger animate-pulse" data-id="element-581" />
                            Camera Live
                          </span>}
                      </div>
                    </td>
                    <td className="px-4" data-id="element-582">
                      {device.assignedUser ? <div className="flex flex-col" data-id="element-583">
                          <span className="text-[14px] text-white" data-id="element-584">
                            {device.assignedUser.fullName}
                          </span>
                          <span className="text-[12px] text-text-secondary" data-id="element-585">
                            {device.assignedUser.email}
                          </span>
                        </div> : <span className="text-[13px] text-text-tertiary" data-id="element-586">
                          Unassigned
                        </span>}
                    </td>
                    <td className="px-4" data-id="element-587">
                      <div className="flex items-center gap-2" data-id="element-588">
                        {isRecentlyActive(device.lastSeenAt) && <div className="w-2 h-2 rounded-full bg-success animate-pulse" data-id="element-589" />}
                        <span className={`text-[13px] ${!device.lastSeenAt ? 'text-text-tertiary' : 'text-text-secondary'}`} data-id="element-590">
                          {getRelativeTime(device.lastSeenAt)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 text-[13px] text-text-secondary font-mono" data-id="element-591">
                      {device.firmwareVersion || '—'}
                    </td>
                    <td className="px-4" data-id="element-592">
                      <div className="flex items-center gap-1" data-id="element-593">
                        <button onClick={() => setViewDevice(device)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-594">
                          <Eye className="w-4 h-4" data-id="element-595" />
                        </button>
                        <button onClick={() => setEditDevice(device)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-596">
                          <Pencil className="w-4 h-4" data-id="element-597" />
                        </button>
                        <button onClick={e => {
                    e.stopPropagation();
                    if (openMenuId === device.id) {
                      setOpenMenuId(null);
                      setMenuPos(null);
                    } else {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenuPos({
                        top: rect.bottom + 4,
                        left: rect.right - 176
                      });
                      setOpenMenuId(device.id);
                    }
                  }} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-598">
                          <MoreHorizontal className="w-4 h-4" data-id="element-599" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dropdown Menu Portal - rendered outside table to avoid overflow clipping */}
      {openMenuId && menuPos && ReactDOM.createPortal(<>
            <div className="fixed inset-0" style={{
        zIndex: 99999
      }} onClick={() => {
        setOpenMenuId(null);
        setMenuPos(null);
      }} data-id="element-600" />
            <div className="fixed w-44 bg-surface-elevated border border-border rounded-card py-1" style={{
        zIndex: 100000,
        top: menuPos.top,
        left: menuPos.left
      }} data-id="element-601">
              <button onClick={() => {
          setOpenMenuId(null);
          setMenuPos(null);
        }} className="w-full text-left px-4 py-2 text-[13px] text-white hover:bg-border first:rounded-t-card" data-id="element-602">
                View Sessions
              </button>
              <button onClick={() => {
          setOpenMenuId(null);
          setMenuPos(null);
        }} className="w-full text-left px-4 py-2 text-[13px] text-white hover:bg-border" data-id="element-603">
                View Sync Logs
              </button>
              <button onClick={() => {
          const device = devices.find(d => d.id === openMenuId);
          if (device) {
            updateDeviceStatus(token!, device.id, device.status === 'active' ? 'inactive' : 'active');
            fetchDevices();
          }
          setOpenMenuId(null);
          setMenuPos(null);
        }} className="w-full text-left px-4 py-2 text-[13px] text-white hover:bg-border" data-id="element-604">
                {devices.find(d => d.id === openMenuId)?.status === 'active' ? 'Deactivate Device' : 'Activate Device'}
              </button>
              <button onClick={() => {
          const device = devices.find(d => d.id === openMenuId);
          if (device) setDeleteDeviceObj(device);
          setOpenMenuId(null);
          setMenuPos(null);
        }} className="w-full text-left px-4 py-2 text-[13px] text-danger hover:bg-border last:rounded-b-card" data-id="element-605">
                Delete Device
              </button>
            </div>
          </>, document.body)}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4" data-id="element-606">
        <div className="flex items-center gap-2" data-id="element-607">
          <select value={limit} onChange={e => {
          setLimit(Number(e.target.value));
          setPage(1);
        }} className="h-[32px] bg-surface border border-border rounded-btn px-2 text-[13px] text-white focus:outline-none focus:border-accent" data-id="element-608">
            <option value={25} data-id="element-609">25 per page</option>
            <option value={50} data-id="element-610">50 per page</option>
            <option value={100} data-id="element-611">100 per page</option>
          </select>
        </div>

        <div className="flex items-center gap-2" data-id="element-612">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-613">
            <ChevronLeft className="w-4 h-4" data-id="element-614" /> Prev
          </button>

          <button className="w-[32px] h-[32px] bg-accent text-background rounded-btn text-[13px] font-medium flex items-center justify-center" data-id="element-615">
            {page}
          </button>

          <button onClick={() => setPage(p => p + 1)} disabled={page * limit >= totalDevices} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-616">
            Next <ChevronRight className="w-4 h-4" data-id="element-617" />
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && <div className={`fixed top-6 right-6 px-4 py-3 rounded-badge shadow-lg flex items-center gap-2 z-50 animate-in slide-in-from-top-5 ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`} data-id="element-618">
          {toast.type === 'success' ? <Check className="w-4 h-4" data-id="element-619" /> : <AlertTriangle className="w-4 h-4" data-id="element-620" />}
          <span className="text-[14px] font-medium" data-id="element-621">{toast.message}</span>
        </div>}

      {/* Modals & Panels */}
      <DeviceModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSuccess={() => {
      setIsAddModalOpen(false);
      showToast('Device created successfully');
      fetchDevices();
    }} data-id="element-622" />

      <CradleModal isOpen={isCradleModalOpen} onClose={() => setIsCradleModalOpen(false)} onSuccess={() => {
      setIsCradleModalOpen(false);
      showToast('Cradle device created');
      fetchDevices();
    }} data-id="element-623" />

      {editDevice && <DeviceModal isOpen={true} device={editDevice} onClose={() => setEditDevice(null)} onSuccess={() => {
      setEditDevice(null);
      showToast('Device updated successfully');
      fetchDevices();
    }} data-id="element-624" />}

      {deleteDeviceObj && <DeleteModal device={deleteDeviceObj} onClose={() => setDeleteDeviceObj(null)} onConfirm={async () => {
      if (!token) return;
      try {
        await deleteDevice(token, deleteDeviceObj.id);
        setDeleteDeviceObj(null);
        showToast('Device deleted successfully');
        fetchDevices();
      } catch (err: any) {
        showToast(err.message || 'Failed to delete device', 'error');
      }
    }} data-id="element-625" />}

      <DeviceDetailPanel device={viewDevice} onClose={() => setViewDevice(null)} onEdit={() => {
      setViewDevice(null);
      setEditDevice(viewDevice);
    }} onDelete={() => {
      setViewDevice(null);
      setDeleteDeviceObj(viewDevice);
    }} onCameraSaved={() => {
      showToast('Camera settings updated');
      fetchDevices();
    }} data-id="element-626" />
    </div>;
}
// --- Subcomponents ---
function DeviceModal({
  isOpen,
  device,
  onClose,
  onSuccess
}: {
  isOpen: boolean;
  device?: Device;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    token
  } = useAuth();
  const [formData, setFormData] = useState({
    deviceUid: device?.deviceUid || '',
    serialNumber: device?.serialNumber || '',
    deviceType: device?.deviceType || '',
    firmwareVersion: device?.firmwareVersion || '',
    hardwareVersion: device?.hardwareVersion || '',
    status: device?.status || 'inactive',
    assignedUserId: device?.assignedUser?.id || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(device?.assignedUser?.id || null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  // Search users for assignment
  useEffect(() => {
    if (!token || userSearch.length < 2 || selectedUserId) {
      setUsers([]);
      setShowUserDropdown(false);
      return;
    }
    setUserSearchLoading(true);
    setUserSearchError('');
    setShowUserDropdown(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiGet(`/admin/users/all?search=${encodeURIComponent(userSearch)}&limit=10`, token);
        setUsers(res.users || res.data || []);
      } catch (e: any) {
        console.error('Failed to search users:', e);
        setUserSearchError('Failed to load users');
        setUsers([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, token, selectedUserId]);
  if (!isOpen) return null;
  const generateId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      if (device) {
        await updateDeviceStatus(token, device.id, formData.status);
        // In a real app, update other fields too
      } else {
        await createDevice(token, formData);
        if (selectedUserId) {
          console.log('TODO: assign device to user', selectedUserId);
        }
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-627">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={onClose} data-id="element-628" />
      <div className="relative w-full max-w-[480px] bg-surface border border-border rounded-card p-8 shadow-2xl max-h-[90vh] overflow-y-auto" data-id="element-629">
        <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-630">
          <X className="w-5 h-5" data-id="element-631" />
        </button>

        <h2 className="text-[18px] font-semibold text-white mb-6" data-id="element-632">
          {device ? 'Edit Device' : 'Add New Device'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-633">
          <div data-id="element-634">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-635">
              Device UID
            </label>
            <div className="flex gap-2" data-id="element-636">
              <input type="text" required value={formData.deviceUid} onChange={e => setFormData({
              ...formData,
              deviceUid: e.target.value.toUpperCase()
            })} className="flex-1 h-[44px] bg-background border border-border rounded-input px-4 text-white font-mono focus:outline-none focus:border-accent uppercase" data-id="element-637" />
              <button type="button" onClick={() => setFormData({
              ...formData,
              deviceUid: generateId()
            })} className="px-4 h-[44px] bg-surface-elevated border border-border rounded-btn text-[13px] text-white hover:bg-border transition-colors" data-id="element-638">
                Generate
              </button>
            </div>
          </div>
          <div data-id="element-639">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-640">
              Serial Number
            </label>
            <div className="flex gap-2" data-id="element-641">
              <input type="text" required value={formData.serialNumber} onChange={e => setFormData({
              ...formData,
              serialNumber: e.target.value.toUpperCase()
            })} className="flex-1 h-[44px] bg-background border border-border rounded-input px-4 text-white font-mono focus:outline-none focus:border-accent uppercase" data-id="element-642" />
              <button type="button" onClick={() => setFormData({
              ...formData,
              serialNumber: `SN-${generateId()}`
            })} className="px-4 h-[44px] bg-surface-elevated border border-border rounded-btn text-[13px] text-white hover:bg-border transition-colors" data-id="element-643">
                Generate
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-644">
            <div data-id="element-645">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-646">
                Device Type
              </label>
              <input type="text" placeholder="Bella Belt" value={formData.deviceType} onChange={e => setFormData({
              ...formData,
              deviceType: e.target.value
            })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent" data-id="element-647" />
            </div>
            <div data-id="element-648">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-649">
                Status
              </label>
              <select value={formData.status} onChange={e => setFormData({
              ...formData,
              status: e.target.value as any
            })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent appearance-none" data-id="element-650">
                <option value="active" data-id="element-651">Active</option>
                <option value="inactive" data-id="element-652">Inactive</option>
                <option value="pending" data-id="element-653">Pending</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-654">
            <div data-id="element-655">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-656">
                Firmware Version
              </label>
              <input type="text" placeholder="1.0.0" value={formData.firmwareVersion} onChange={e => setFormData({
              ...formData,
              firmwareVersion: e.target.value
            })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent font-mono" data-id="element-657" />
            </div>
            <div data-id="element-658">
              <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-659">
                Hardware Version
              </label>
              <input type="text" placeholder="1.0" value={formData.hardwareVersion} onChange={e => setFormData({
              ...formData,
              hardwareVersion: e.target.value
            })} className="w-full h-[44px] bg-background border border-border rounded-input px-4 text-white focus:outline-none focus:border-accent font-mono" data-id="element-660" />
            </div>
          </div>

          <div data-id="element-661">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-662">
              Assign to User (Optional)
            </label>
            <div className="relative" data-id="element-663">
              <input type="text" placeholder="Search user by name or email..." value={userSearch} onChange={e => {
              setUserSearch(e.target.value);
              if (selectedUserId) {
                setSelectedUserId(null);
              }
            }} className="w-full h-[44px] bg-background border border-border rounded-input px-4 pr-10 text-white focus:outline-none focus:border-accent" data-id="element-664" />
              {selectedUserId && <button type="button" onClick={() => {
              setSelectedUserId(null);
              setUserSearch('');
              setUsers([]);
              setShowUserDropdown(false);
            }} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-white transition-colors" data-id="element-665">
                  <X className="w-3.5 h-3.5" data-id="element-666" />
                </button>}
              {showUserDropdown && userSearch.length >= 2 && !selectedUserId && <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-input shadow-lg z-10 max-h-[200px] overflow-y-auto" data-id="element-667">
                    {userSearchLoading ? <div className="flex items-center justify-center py-4" data-id="element-668">
                        <Loader2 className="w-4 h-4 animate-spin text-text-secondary" data-id="element-669" />
                      </div> : userSearchError ? <div className="px-4 py-3 text-center text-[13px] text-accent" data-id="element-670">
                        {userSearchError}
                      </div> : users.length === 0 ? <div className="px-4 py-3 text-center text-[13px] text-text-tertiary" data-id="element-671">
                        No users found
                      </div> : users.map(u => <button key={u.id} type="button" onClick={() => {
                setSelectedUserId(u.id);
                setUserSearch(u.fullName);
                setUsers([]);
                setShowUserDropdown(false);
              }} className="w-full text-left px-4 py-2.5 hover:bg-[#1E1E1E] text-[13px] text-white flex flex-col border-b border-border last:border-b-0 transition-colors" data-id="element-672">
                          <span className="font-medium" data-id="element-673">{u.fullName}</span>
                          <span className="text-text-secondary text-[12px]" data-id="element-674">
                            {u.email}
                          </span>
                        </button>)}
                  </div>}
            </div>
          </div>

          {error && <p className="text-danger text-[13px]" data-id="element-675">{error}</p>}

          <button type="submit" disabled={isLoading} className="w-full h-[44px] bg-accent text-background font-semibold rounded-btn mt-6 flex items-center justify-center hover:opacity-90 disabled:opacity-50" data-id="element-676">
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-677" /> : device ? 'Save Changes' : 'Create Device'}
          </button>
        </form>
      </div>
    </div>;
}
function CradleModal({
  isOpen,
  onClose,
  onSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const {
    token
  } = useAuth();
  const [deviceUid, setDeviceUid] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (isOpen) {
      setDeviceUid('');
      setSerialNumber('');
      setError('');
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const generateId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      await createCradleDevice(token, {
        deviceUid: deviceUid.trim(),
        serialNumber: serialNumber.trim()
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create cradle device.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-678">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={onClose} data-id="element-679" />
      <div className="relative w-full max-w-[440px] bg-surface border border-border rounded-card p-8 shadow-2xl" data-id="element-680">
        <button type="button" onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-681">
          <X className="w-5 h-5" data-id="element-682" />
        </button>

        <div className="flex items-center gap-3 mb-6" data-id="element-683">
          <div className="w-10 h-10 rounded-full bg-[rgba(107,33,232,0.12)] border border-[rgba(107,33,232,0.3)] flex items-center justify-center" data-id="element-684">
            <Radio className="w-5 h-5 text-accent" data-id="element-685" />
          </div>
          <div data-id="element-686">
            <h2 className="text-[18px] font-semibold text-white" data-id="element-687">
              Create Cradle Device
            </h2>
            <p className="text-[12px] text-text-secondary mt-0.5" data-id="element-688">
              Register a new cradle to the fleet
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-689">
          <div data-id="element-690">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-691">
              Device UID <span className="text-danger" data-id="element-692">*</span>
            </label>
            <div className="flex gap-2" data-id="element-693">
              <input type="text" required value={deviceUid} onChange={e => setDeviceUid(e.target.value.toUpperCase())} className="flex-1 h-[44px] bg-background border border-border rounded-input px-4 text-white font-mono focus:outline-none focus:border-accent uppercase" data-id="element-694" />
              <button type="button" onClick={() => setDeviceUid(generateId())} className="px-4 h-[44px] bg-surface-elevated border border-border rounded-btn text-[13px] text-white hover:bg-border transition-colors" data-id="element-695">
                Generate
              </button>
            </div>
          </div>

          <div data-id="element-696">
            <label className="block text-[13px] text-text-secondary mb-1.5" data-id="element-697">
              Serial Number <span className="text-danger" data-id="element-698">*</span>
            </label>
            <div className="flex gap-2" data-id="element-699">
              <input type="text" required value={serialNumber} onChange={e => setSerialNumber(e.target.value.toUpperCase())} className="flex-1 h-[44px] bg-background border border-border rounded-input px-4 text-white font-mono focus:outline-none focus:border-accent uppercase" data-id="element-700" />
              <button type="button" onClick={() => setSerialNumber(`SN-${generateId()}`)} className="px-4 h-[44px] bg-surface-elevated border border-border rounded-btn text-[13px] text-white hover:bg-border transition-colors" data-id="element-701">
                Generate
              </button>
            </div>
          </div>

          {error && <div className="p-3 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-input" data-id="element-702">
              <p className="text-danger text-[13px]" data-id="element-703">{error}</p>
            </div>}

          <button type="submit" disabled={isLoading} className="w-full h-[44px] bg-accent text-white font-semibold rounded-btn mt-2 flex items-center justify-center hover:opacity-90 disabled:opacity-50" data-id="element-704">
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-705" /> : 'Create Cradle Device'}
          </button>
        </form>
      </div>
    </div>;
}
function DeleteModal({
  device,
  onClose,
  onConfirm
}: {
  device: Device;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-706">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={onClose} data-id="element-707" />
      <div className="relative w-full max-w-[360px] bg-surface border border-border rounded-card p-6 text-center shadow-2xl" data-id="element-708">
        <div className="w-16 h-16 rounded-full bg-[rgba(239,68,68,0.1)] flex items-center justify-center mx-auto mb-4" data-id="element-709">
          <AlertTriangle className="w-8 h-8 text-danger" data-id="element-710" />
        </div>
        <h2 className="text-[16px] font-semibold text-white mb-2" data-id="element-711">
          Delete this device?
        </h2>
        <p className="text-[14px] text-text-secondary mb-6" data-id="element-712">
          This will permanently delete device{' '}
          <strong className="text-white font-mono" data-id="element-713">{device.deviceUid}</strong>.
          This cannot be undone.
        </p>
        <div className="flex gap-3" data-id="element-714">
          <button onClick={onClose} className="flex-1 h-[40px] bg-surface border border-border text-white rounded-btn font-medium hover:bg-border transition-colors" data-id="element-715">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 h-[40px] bg-danger text-white rounded-btn font-medium hover:opacity-90 transition-opacity" data-id="element-716">
            Delete
          </button>
        </div>
      </div>
    </div>;
}
function DeviceDetailPanel({
  device,
  onClose,
  onEdit,
  onDelete,
  onCameraSaved
}: {
  device: Device | null;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCameraSaved: () => void;
}) {
  const {
    token
  } = useAuth();
  const [cameraName, setCameraName] = useState('');
  const [streamType, setStreamType] = useState<'mjpeg' | 'hls' | 'iframe'>('mjpeg');
  const [streamUrl, setStreamUrl] = useState('');
  const [posterUrl, setPosterUrl] = useState('');
  const [cameraSaving, setCameraSaving] = useState(false);
  const [cameraMessage, setCameraMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  useEffect(() => {
    if (device) {
      setCameraName(device.cameraName || '');
      setStreamType(device.streamType as 'mjpeg' | 'hls' | 'iframe' || 'mjpeg');
      setStreamUrl(device.streamUrl || '');
      setPosterUrl(device.posterUrl || '');
      setCameraMessage(null);
    }
  }, [device]);
  if (!device) return null;
  const handleSaveCamera = async () => {
    if (!token) return;
    setCameraSaving(true);
    setCameraMessage(null);
    try {
      await updateDeviceCamera(token, device.id, {
        cameraName: cameraName.trim() || null,
        streamType,
        streamUrl: streamUrl.trim() || null,
        posterUrl: posterUrl.trim() || null
      });
      setCameraMessage({
        type: 'success',
        text: 'Camera settings saved.'
      });
      onCameraSaved();
    } catch (err: any) {
      setCameraMessage({
        type: 'error',
        text: err.message || 'Failed to save camera settings.'
      });
    } finally {
      setCameraSaving(false);
    }
  };
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-717">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-718" />
      <div className="relative w-[440px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-719">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-720">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-721">
            <X className="w-5 h-5" data-id="element-722" />
          </button>
          <div className="flex items-center gap-4" data-id="element-723">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center flex-shrink-0" data-id="element-724">
              <Cpu className="w-6 h-6 text-white" data-id="element-725" />
            </div>
            <div data-id="element-726">
              <h2 className="text-[18px] font-semibold text-white font-mono" data-id="element-727">
                {device.deviceUid}
              </h2>
              <div className="mt-1 flex items-center gap-2 flex-wrap" data-id="element-728">
                {device.status === 'active' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-729">
                    Active
                  </span>}
                {device.status === 'inactive' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(138,138,138,0.1)] text-text-secondary text-[12px]" data-id="element-730">
                    Inactive
                  </span>}
                {device.status === 'pending' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)]" data-id="element-731">
                    Pending
                  </span>}
                {device.isLive && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[11px] border border-[rgba(239,68,68,0.25)] inline-flex items-center gap-1.5" data-id="element-732">
                    <span className="w-1.5 h-1.5 rounded-full bg-danger animate-pulse" data-id="element-733" />
                    Camera Live
                  </span>}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-734">
          {/* Info */}
          <div className="space-y-4" data-id="element-735">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-736">
              Information
            </h3>
            <div className="space-y-3" data-id="element-737">
              <div className="flex justify-between" data-id="element-738">
                <span className="text-[13px] text-text-secondary" data-id="element-739">
                  Serial Number
                </span>
                <span className="text-[13px] text-white font-mono" data-id="element-740">
                  {device.serialNumber}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-741">
                <span className="text-[13px] text-text-secondary" data-id="element-742">Type</span>
                <span className="text-[13px] text-white" data-id="element-743">
                  {device.deviceType || 'Bella Belt'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-744">
                <span className="text-[13px] text-text-secondary" data-id="element-745">
                  Firmware
                </span>
                <span className="text-[13px] text-white font-mono" data-id="element-746">
                  {device.firmwareVersion || '—'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-747">
                <span className="text-[13px] text-text-secondary" data-id="element-748">
                  Hardware
                </span>
                <span className="text-[13px] text-white font-mono" data-id="element-749">
                  {device.hardwareVersion || '—'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-750">
                <span className="text-[13px] text-text-secondary" data-id="element-751">
                  Last Seen
                </span>
                <span className="text-[13px] text-white" data-id="element-752">
                  {device.lastSeenAt ? new Date(device.lastSeenAt).toLocaleString() : 'Never'}
                </span>
              </div>
            </div>
          </div>

          {/* Camera Settings */}
          <div className="space-y-4" data-id="element-753">
            <div className="flex items-center justify-between" data-id="element-754">
              <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] flex items-center gap-2" data-id="element-755">
                <Video className="w-3.5 h-3.5" data-id="element-756" />
                Camera Settings
              </h3>
              {device.isLive && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[11px] border border-[rgba(239,68,68,0.25)] inline-flex items-center gap-1.5" data-id="element-757">
                  <span className="w-1.5 h-1.5 rounded-full bg-danger animate-pulse" data-id="element-758" />
                  Live
                </span>}
            </div>

            <div className="space-y-3" data-id="element-759">
              <div data-id="element-760">
                <label className="block text-[12px] text-text-secondary mb-1" data-id="element-761">
                  Camera Name
                </label>
                <input type="text" value={cameraName} onChange={e => setCameraName(e.target.value)} placeholder="e.g. Nursery Cam" className="w-full h-[36px] bg-background border border-border rounded-input px-3 text-[13px] text-white focus:outline-none focus:border-accent" data-id="element-762" />
              </div>

              <div data-id="element-763">
                <label className="block text-[12px] text-text-secondary mb-1" data-id="element-764">
                  Stream Type
                </label>
                <select value={streamType} onChange={e => setStreamType(e.target.value as 'mjpeg' | 'hls' | 'iframe')} className="w-full h-[36px] bg-background border border-border rounded-input px-3 text-[13px] text-white focus:outline-none focus:border-accent" data-id="element-765">
                  <option value="mjpeg" data-id="element-766">MJPEG</option>
                  <option value="hls" data-id="element-767">HLS</option>
                  <option value="iframe" data-id="element-768">iframe</option>
                </select>
              </div>

              <div data-id="element-769">
                <label className="block text-[12px] text-text-secondary mb-1" data-id="element-770">
                  Stream URL
                </label>
                <input type="url" value={streamUrl} onChange={e => setStreamUrl(e.target.value)} placeholder="https://..." className="w-full h-[36px] bg-background border border-border rounded-input px-3 text-[13px] text-white font-mono focus:outline-none focus:border-accent" data-id="element-771" />
              </div>

              <div data-id="element-772">
                <label className="block text-[12px] text-text-secondary mb-1" data-id="element-773">
                  Poster URL{' '}
                  <span className="text-text-tertiary" data-id="element-774">(optional)</span>
                </label>
                <input type="url" value={posterUrl} onChange={e => setPosterUrl(e.target.value)} placeholder="https://..." className="w-full h-[36px] bg-background border border-border rounded-input px-3 text-[13px] text-white font-mono focus:outline-none focus:border-accent" data-id="element-775" />
              </div>

              {cameraMessage && <p className={`text-[12px] ${cameraMessage.type === 'success' ? 'text-success' : 'text-danger'}`} data-id="element-776">
                  {cameraMessage.text}
                </p>}

              <button onClick={handleSaveCamera} disabled={cameraSaving} className="w-full h-[36px] bg-accent text-white rounded-btn text-[13px] font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-777">
                {cameraSaving ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-778" /> : 'Save Camera Settings'}
              </button>
            </div>
          </div>

          {/* Assignment */}
          <div className="space-y-4" data-id="element-779">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-780">
              Assignment
            </h3>
            {device.assignedUser ? <div className="bg-background border border-border rounded-card p-4" data-id="element-781">
                <div className="flex justify-between items-start mb-3" data-id="element-782">
                  <div data-id="element-783">
                    <p className="text-[14px] font-medium text-white" data-id="element-784">
                      {device.assignedUser.fullName}
                    </p>
                    <p className="text-[13px] text-text-secondary" data-id="element-785">
                      {device.assignedUser.email}
                    </p>
                  </div>
                  <button className="text-[13px] text-danger hover:text-red-400 transition-colors" data-id="element-786">
                    Unassign
                  </button>
                </div>
                <button className="w-full h-[32px] bg-surface border border-border rounded-btn text-[13px] text-white hover:bg-border transition-colors" data-id="element-787">
                  Reassign
                </button>
              </div> : <div className="bg-background border border-border rounded-card p-4 text-center" data-id="element-788">
                <p className="text-[13px] text-text-secondary mb-3" data-id="element-789">
                  Device is currently unassigned.
                </p>
                <button className="h-[32px] px-4 bg-surface border border-border rounded-btn text-[13px] text-white hover:bg-border transition-colors" data-id="element-790">
                  Assign User
                </button>
              </div>}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border space-y-3" data-id="element-791">
          <button onClick={onEdit} className="w-full h-[40px] bg-surface border border-border text-white rounded-btn font-medium hover:bg-border transition-colors" data-id="element-792">
            Edit Device
          </button>
          <button onClick={onDelete} className="w-full h-[40px] bg-[rgba(239,68,68,0.1)] text-danger border border-[rgba(239,68,68,0.2)] rounded-btn font-medium hover:bg-[rgba(239,68,68,0.2)] transition-colors" data-id="element-793">
            Delete Device
          </button>
        </div>
      </div>
    </div>;
}